/* eslint-env browser */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import { showToast } from '../feedback/feedbackStore';

// English names shown beside each language's own name, so a colleague who
// cannot read the script still finds their way back.
const ENGLISH_NAMES = { en: 'English', am: 'Amharic', om: 'Oromo', ti: 'Tigrinya', ar: 'Arabic', so: 'Somali', fr: 'French' };

const englishNameOf = (locale) => {
  if (ENGLISH_NAMES[locale]) return ENGLISH_NAMES[locale];
  try {
    const names = new Intl.DisplayNames(['en'], { type: 'language' });
    return names.of(locale) || locale;
  } catch (e) {
    return locale;
  }
};

const readSavedLocale = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('lang') || 'null');
    return saved && saved.langLocale ? saved.langLocale : null;
  } catch (e) {
    return null;
  }
};

const saveLocal = (langLocale, dateLocale) => {
  try {
    localStorage.setItem('lang', JSON.stringify({ langLocale, dateLocale }));
  } catch (e) {
    // private window or storage blocked: the choice lasts for this visit
  }
};

/**
 * The portal's language switch, shared by the signed-in shell and the
 * public header. Switching loads the pack once (api.loadLanguagePack), applies
 * it, remembers it on this computer (localStorage 'lang', the key AuthLoading
 * reads at start) and, for a signed-in person, saves it to their profile
 * (/users/{uid}/lang - one small write, no reads).
 *
 * applyProfileLanguage: on a computer with no saved choice, switch once to
 * the language saved on the signed-in profile.
 */
export default function useLanguageSwitcher({ applyProfileLanguage = false } = {}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const langlist = useSelector((state) => state.languagedata.langlist);
  const profile = useSelector((state) => state.auth.profile);
  const [pendingLocale, setPendingLocale] = useState(null);
  const requestRef = useRef(0);
  const profileAppliedRef = useRef(false);

  const languages = useMemo(() => {
    const list = Array.isArray(langlist) ? langlist : langlist ? Object.values(langlist) : [];
    return list
      .filter((l) => l && l.langLocale)
      .map((l) => ({
        id: l.id,
        langLocale: l.langLocale,
        dateLocale: l.dateLocale || l.langLocale,
        name: l.langName || englishNameOf(l.langLocale),
        englishName: englishNameOf(l.langLocale),
      }));
  }, [langlist]);

  const currentLocale = i18n.resolvedLanguage || i18n.language;
  const current = languages.find((l) => l.langLocale === currentLocale) || null;
  const uid = profile && profile.uid ? profile.uid : null;
  const profileLocale = profile && profile.lang && profile.lang.langLocale ? profile.lang.langLocale : null;

  const switchTo = useCallback(
    async (langLocale, { saveToProfile = true } = {}) => {
      const entry = languages.find((l) => l.langLocale === langLocale);
      if (!entry) return false;
      const request = ++requestRef.current;
      setPendingLocale(langLocale);
      let pack = null;
      try {
        pack = await dispatch(api.loadLanguagePack(langLocale));
      } catch (e) {
        pack = null;
      }
      if (request !== requestRef.current) return false; // a newer choice won
      setPendingLocale(null);
      if (!pack || !pack.success) {
        showToast({ message: t('no_internet_connection'), kind: 'error' });
        return false;
      }
      i18n.addResourceBundle(langLocale, 'translations', pack.keyValuePairs, true, true);
      await i18n.changeLanguage(langLocale);
      moment.locale(entry.dateLocale);
      saveLocal(langLocale, entry.dateLocale);
      if (saveToProfile && uid && profileLocale !== langLocale) {
        dispatch(api.updateProfile({ lang: { langLocale, dateLocale: entry.dateLocale } }));
      }
      return true;
    },
    [languages, dispatch, i18n, t, uid, profileLocale]
  );

  useEffect(() => {
    if (!applyProfileLanguage || profileAppliedRef.current) return;
    if (!uid || !profileLocale || languages.length === 0) return;
    profileAppliedRef.current = true;
    if (readSavedLocale()) return; // this computer already has a choice
    if (profileLocale === currentLocale) return;
    switchTo(profileLocale, { saveToProfile: false });
  }, [applyProfileLanguage, uid, profileLocale, languages.length, currentLocale, switchTo]);

  return { languages, current, currentLocale, pendingLocale, switchTo, signedIn: !!uid };
}
